
import { Injectable, DOCUMENT, inject, signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DesignModeService {
  protected readonly _document = inject<Document>(DOCUMENT);

  // The current theme as a signal, so templates follow a switch without a reload. The app is zoneless,
  // and the switch runs inside a view transition callback that schedules no change detection (MOG-4912).
  private readonly _theme = signal<string | null>(null);
  public readonly theme = this._theme.asReadonly();

  public init(): void {
    const html = this._document.getElementsByTagName('html')[0];
    const dataTheme = localStorage.getItem('data-bs-theme');

    if (!!dataTheme) {
      html.setAttribute('data-bs-theme', dataTheme);
    } else {
      localStorage.setItem('data-bs-theme', 'dark');
      html.setAttribute('data-bs-theme', 'dark');
    }
    this._theme.set(html.getAttribute('data-bs-theme'));
  }

  public toggleStatus(): void {
    const html = this._document.getElementsByTagName('html')[0];
    html.getAttribute('data-bs-theme') === 'dark'
      ? html.setAttribute('data-bs-theme', 'light')
      : html.setAttribute('data-bs-theme', 'dark');

    localStorage.setItem('data-bs-theme', html.getAttribute('data-bs-theme') ?? 'dark');
    this._theme.set(html.getAttribute('data-bs-theme'));
  }

  /**
   * Toggle theme with animated circle reveal effect using View Transitions API.
   * Falls back to instant toggle if View Transitions API is not supported.
   */
  public async toggleStatusAnimated(buttonElement: HTMLElement, duration: number = 400): Promise<void> {
    // Fallback for browsers that don't support View Transitions API
    if (!this._document.startViewTransition) {
      this.toggleStatus();
      return;
    }

    const transition = this._document.startViewTransition(() => {
      this.toggleStatus();
    });

    await transition.ready;

    // Calculate animation origin from button center
    const { top, left, width, height } = buttonElement.getBoundingClientRect();
    const x = left + width / 2;
    const y = top + height / 2;

    // Calculate the maximum radius needed to cover the entire viewport
    const maxRadius = Math.hypot(
      Math.max(left, window.innerWidth - left),
      Math.max(top, window.innerHeight - top)
    );

    // Animate the new view with a circular clip-path reveal
    this._document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${maxRadius}px at ${x}px ${y}px)`
        ]
      },
      {
        duration,
        easing: 'ease-in-out',
        pseudoElement: '::view-transition-new(root)'
      }
    );
  }

  get dataTheme(): string | null {
    const html = this._document.getElementsByTagName('html')[0];

    return html.getAttribute('data-bs-theme');
  }
}
